import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';

// types
import { PdpSharedAuctionPreview_item$key as Item } from './__generated__/PdpSharedAuctionPreview_item.graphql';
import { PdpSharedAuctionPreview_viewer$key as Viewer } from './__generated__/PdpSharedAuctionPreview_viewer.graphql';

// styles
import styles from './PdpSharedAuctionPreview.scss';

type Props = {
    item: Item;
    viewer: Viewer;
    currency: string;
    promoCode: ReactNode;
};

const itemFragment = graphql`
    fragment PdpSharedAuctionPreview_item on Item {
        ...PriceDisplay_item
            @arguments(userId: $userId, isTrade: $isTrade, userCountryCode: $userCountryCode)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...OriginalListPrice_item
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedAuctionPreview_viewer on Viewer {
        originalListPricePdpFF: featureFlag(feature: "originalListPricePdp")
    }
`;

export const PdpSharedAuctionPreview: FC<Props> = ({
    item: itemRef,
    viewer: viewerRef,
    currency,
    promoCode,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { buyItNowAmount, hasReserve } = useAuctionPricing({
        item,
        currency,
    });
    const originalListPricePdpFF = viewer?.originalListPricePdpFF;

    return (
        <div className={styles.container}>
            {originalListPricePdpFF && (
                <OriginalListPrice
                    item={item}
                    currency={currency}
                    className={styles.originalListPrice}
                />
            )}
            {!hasReserve && (
                <span className={styles.noReserve}>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.noReserve"
                        defaultMessage="No Reserve"
                    />
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <span className={styles.bid}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.startingBid"
                    defaultMessage="Starting Bid"
                />
            </span>
            <PriceDisplay currency={currency} item={item} useSvgIcon />
            {buyItNowAmount && (
                <span>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.currentBid"
                        defaultMessage="{amount} Buy It Now"
                        values={{
                            amount: <FormattedPrice value={buyItNowAmount} currency={currency} />,
                        }}
                    />
                </span>
            )}
            <div className={styles.promoCode}>{promoCode}</div>
            <div className={styles.prompt}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.prompt"
                    defaultMessage="You can't bid on this item yet. Favorite this item to get updates on this auction. {link}"
                    values={{
                        link: (
                            <Link
                                href="/info/auctions-faq/"
                                target="_blank"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionPreview.learnMore"
                                    defaultMessage="Learn More about Auctions"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        </div>
    );
};
